// react
import React, { useState, useEffect } from "react";

// react-router-dom
import { useLocation } from "react-router-dom";

// context
import { UserAuth } from "context/AuthContext";

// react-i18next
import { useTranslation } from "react-i18next";

// @mui
import {
  useMediaQuery,
  Box,
  Grid,
  MenuItem,
  SwipeableDrawer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  TextField,
  Typography,
  FormControl,
  FormGroup,
  FormControlLabel,
  Select,
  Switch,
  Tooltip,
} from "@mui/material";
import { CloseOutlined as CloseOutlinedIcon, FileUpload as FileUploadIcon } from "@mui/icons-material";

// components
import { LinearLoadingComponent } from "components/loading/LoadingComponent";
import { Puller } from "components/common/Puller";
import { NewCertConfirm, NewRequestConfirm, InvalidFile } from "components/app/AppFeedback";

// lib/safetwin
import { decryptSeedByPasswordHash4Ed25519 } from "lib/safetwin/crypto/cryptoseed";

// artifacts
import genImgRecord from "artifacts/img/core/genImgRecord";

// features/databox
import sendCertificationRequest from "features/databox/sendCertificationRequest";
import completeCertificationRequest from "features/databox/completeCertificationRequest";

// utils
import fileToUint8Array from "utils/fileToUint8Array";

// services/storage
import uploadFile from "services/storage/uploadFile";
import getFile from "services/storage/getFile";

// firebase
import { getFunctions, httpsCallable } from "firebase/functions";

// A ---------------------------------------------------------------------- M

const getButtonStyle = (action) => ({
  py: 0.5,
  borderColor: "divider",
  color: action === "close" ? "error.main" : "primary.main",
  textTransform: "none",
  boxShadow: "none",
  borderRadius: 10,
  "&:hover": {
    borderColor: action === "close" ? "error.dark" : "primary.dark",
    backgroundColor: "action.hover",
  },
});

const MAX_FILE_SIZE = 25 * 1024 * 1024;

const ImageGenerator = ({ tags, tag, types, setTypes, open, setOpen, handleOpenCertificationSuccessful, handleOpenCertificationError, request, setRequest }) => {
  const { user } = UserAuth();
  const location = useLocation();
  const { t } = useTranslation();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const [keypair, setKeyPair] = useState(null);
  const [source, setSource] = useState("");
  const [blobURL, setBlobURL] = useState("");
  const [storagePath, setStoragePath] = useState("");
  const [fileName, setFileName] = useState("");
  const [fileType, setFileType] = useState("");
  const [caption, setCaption] = useState("");
  const [selectedTag, setSelectedTag] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const [tagSelectionOpen, setTagSelectionOpen] = useState(false);
  const [isPublic, setIsPublic] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [approval, setApproval] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isInvalidFile, setIsInvalidFile] = useState(false);

  useEffect(() => {
    const handlePopstate = () => {
      if (open) handleReset();
    };
    window.addEventListener("popstate", handlePopstate);
    return () => window.removeEventListener("popstate", handlePopstate);
  }, [open]);

  useEffect(() => {
    if (open) {
      const keypair = decryptSeedByPasswordHash4Ed25519(user.reloadUserInfo.passwordHash);
      setKeyPair(keypair);
    }
  }, [open]);

  useEffect(() => {
    if (request) {
      const path = request.data.storagePath;
      setStoragePath(path);
      setFileName(request.data.fileName);
      setFileType(request.data.fileType);
      setCaption(request.data.caption);
      setIsPublic(request.public);

      const fetchFile = async () => {
        const bytearray = await getFile(path);
        setSource(bytearray);
      };

      fetchFile();
    } else {
      setSource("");
      setBlobURL("");
      setStoragePath("");
      setFileName("");
      setFileType("");
      setCaption("");
      setIsPublic(false);
    }
  }, [request]);

  const handleGenerate = async () => {
    setConfirm(false);
    setIsLoading(true);

    if (!keypair) {
      console.log("No keypair provided.");
      setIsLoading(false);
      return;
    }

    try {
      let path = storagePath;

      if (!request) {
        const res = await uploadFile(source, fileName, fileType, tag, isPublic, user);
        path = res.storagePath;
        setStoragePath(path);
      }

      const dataBody = { source, storagePath: path, fileName, fileType, caption };
      let result;

      if (tag) result = await genImgRecord(isPublic, user.uid, keypair, dataBody, tag);
      else if (selectedTag) result = await genImgRecord(isPublic, user.uid, keypair, dataBody, selectedTag);
      else result = await genImgRecord(isPublic, user.uid, keypair, dataBody);

      if (result.success) {
        if (request) await completeCertificationRequest(tag, request.id);
        handleOpenCertificationSuccessful();
        if (types && !types.includes("img")) setTypes([...types, "img"]);
      } else {
        handleOpenCertificationError();
      }
    } catch (error) {
      console.error("Error in handleGenerate:", error);
      handleOpenCertificationError();
    } finally {
      setIsLoading(false);
      handleReset();
    }

    setIsLoading(false);
    handleReset();
  };

  const handleSendApprovalRequest = async () => {
    setApproval(false);
    setIsLoading(true);

    try {
      const res = await uploadFile(source, fileName, fileType, tag, isPublic, user);
      const requestTag = tag || selectedTag;
      const requestData = { storagePath: res.storagePath, fileName, fileType, caption };
      await sendCertificationRequest("img", user.uid, isPublic, requestData, requestTag);
    } catch (error) {
      console.error("Error in handleSendApprovalRequest:", error);
      handleOpenCertificationError();
    } finally {
      setIsLoading(false);
      handleReset();
    }
  };

  const handleCapture = async (target) => {
    if (target?.files?.length) {
      const file = target.files[0];
      if (file.size > MAX_FILE_SIZE) {
        setErrorMessage("File size exceeds the 25MB limit.");
        setIsInvalidFile(true);
        resetFileInput();
        return;
      }

      const ext = file.name.split(".").pop().toLowerCase();
      if (["png", "jpg", "jpeg"].includes(ext)) {
        const arr = await fileToUint8Array(file);
        const url = URL.createObjectURL(file);
        setSource(arr);
        setBlobURL(url);
        setFileName(file.name);
        setFileType(file.type);
      } else {
        setErrorMessage(`Invalid format: ${ext}. Allowed: png, jpg, jpeg.`);
        setIsInvalidFile(true);
        resetFileInput();
      }
    }
  };

  const resetFileInput = () => {
    const inp = document.getElementById("certify-image-button");
    if (inp) inp.value = null;
  };

  const handleReset = () => {
    setSource("");
    blobURL && URL.revokeObjectURL(blobURL);
    setBlobURL("");
    setStoragePath("");
    setFileName("");
    setFileType("");
    setCaption("");
    setSelectedTag("");
    setIsPublic(false);
    setIsInvalidFile(false);
    if (request) setRequest(null);
    setOpen(false);
    resetFileInput();
  };

  const handleChange = (e) => setSelectedTag(e.target.value);

  const handleOpen = () => {
    setTagSelectionOpen(true);
    window.history.pushState(null, "");
  };

  const handleClose = () => setTagSelectionOpen(false);

  const handleCloseDialogImage = () => setSelectedImage(null);

  const handleImageClick = async (path) => {
    try {
      let url = path;

      if (typeof path === "string" && !path.startsWith("http://") && !path.startsWith("https://") && !path.startsWith("blob:")) {
        const functions = getFunctions();
        const getSignedUrl = httpsCallable(functions, "getSignedDownloadUrl");

        const tagId = tag || selectedTag || request?.tagId;
        if (!tagId) {
          console.warn("Missing tagId for secure image preview, skipping");
          return;
        }

        const response = await getSignedUrl({ storagePath: path, tagId });
        url = response.data.url;
      }

      const resp = await fetch(url);
      const blob = await resp.blob();

      const validMimeTypes = ["image/jpeg", "image/jpg", "image/png"];
      if (validMimeTypes.includes(blob.type)) {
        setSelectedImage(url);
      } else {
        console.warn(`Unsupported image type for preview: ${blob.type}`);
      }
    } catch (err) {
      console.error("Error previewing image:", err);
    }
  };

  const DialogTitleContent = () => (
    <Grid container alignItems="center" justifyContent="space-between">
      <Typography variant="h6" fontWeight="bold">
        {t("certify_image")}
      </Typography>
      <IconButton onClick={handleReset} color="error" edge="end">
        <CloseOutlinedIcon />
      </IconButton>
    </Grid>
  );

  const ImageReportForm = (
    <Grid container spacing={2}>
      {request ? (
        <Grid item container alignItems="center">
          <Grid item xs={12}>
            <Button onClick={() => handleImageClick(storagePath)} sx={getButtonStyle("open")}>
              {fileName || t("selected_file")}
            </Button>
          </Grid>
        </Grid>
      ) : (
        <Grid item container alignItems="center" spacing={2}>
          <Grid item xs={12} sm={11}>
            <input id="certify-image-button" type="file" accept="image/png,image/jpeg" style={{ display: "none" }} disabled={request} onChange={(e) => handleCapture(e.target)} />
            <label htmlFor="certify-image-button">
              <Button disabled={request} variant="outlined" component="span" startIcon={<FileUploadIcon />} sx={getButtonStyle("open")}>
                {t("select_file")}
              </Button>
            </label>
          </Grid>
          {source ? (
            <Grid item xs={12}>
              <Button onClick={() => handleImageClick(blobURL)} variant="outlined" sx={getButtonStyle("open")}>
                {fileName || t("selected_file")}
              </Button>
            </Grid>
          ) : (
            <Grid item xs={12}>
              <Typography>{t("no_file_selected")}</Typography>
            </Grid>
          )}
        </Grid>
      )}

      <Grid item xs={12}>
        <TextField margin="dense" label={t("description")} variant="outlined" fullWidth value={caption} disabled={request} onChange={(e) => setCaption(e.target.value)} />
      </Grid>

      {location.pathname === "/my-certifications" && tags && (
        <Grid item xs={12}>
          <Typography gutterBottom>{t("databox")}</Typography>
          <FormControl fullWidth>
            <Select
              id="selected-tag-image"
              value={selectedTag}
              displayEmpty
              onChange={handleChange}
              open={tagSelectionOpen}
              onOpen={handleOpen}
              onClose={handleClose}
              sx={{ width: isMobile ? "100%" : "50%" }}
            >
              <MenuItem value="">
                <Typography color="gray">{t("no_databox")}</Typography>
              </MenuItem>
              {tags
                .sort((a, b) => a.nickname.localeCompare(b.nickname))
                .map((tag) => (
                  <MenuItem key={tag.id} value={tag.id}>
                    {tag.nickname}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Grid>
      )}

      <Grid item xs={12}>
        <FormGroup>
          <FormControlLabel control={<Switch checked={isPublic} disabled={request} onChange={(e) => setIsPublic(e.target.checked)} />} label={t("mark_as_public")} />
        </FormGroup>
      </Grid>
    </Grid>
  );

  return (
    <>
      {isMobile ? (
        <SwipeableDrawer
          anchor="bottom"
          open={open}
          onClose={handleReset}
          onOpen={() => setOpen(true)}
          sx={{
            "& .MuiDrawer-paper": {
              width: "100%",
              height: "80%",
              borderTopLeftRadius: "4%",
              borderTopRightRadius: "4%",
            },
          }}
        >
          <Puller />
          <Box sx={{ p: 3 }}>
            <Typography variant="h5" fontWeight="bold" textAlign="center" mt={2} mb={3}>
              {t("certify_image")}
            </Typography>
            {isLoading ? (
              <LinearLoadingComponent />
            ) : (
              <>
                {ImageReportForm}
                <Grid container spacing={1} mt={5}>
                  <Grid item xs={12}>
                    <Button fullWidth variant="outlined" sx={getButtonStyle("close")} onClick={handleReset}>
                      {t("cancel")}
                    </Button>
                  </Grid>
                  {!request && (
                    <Grid item xs={12}>
                      <Button
                        fullWidth
                        variant="outlined"
                        sx={getButtonStyle("open")}
                        disabled={isLoading || (location.pathname === "/my-certifications" && (!source || !selectedTag)) || (location.pathname !== "/my-certifications" && !source)}
                        onClick={() => setApproval(true)}
                      >
                        {t("send_request")}
                      </Button>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <Button fullWidth variant="outlined" sx={getButtonStyle("open")} disabled={isLoading || !source} onClick={() => setConfirm(true)}>
                      {t("certify")}
                    </Button>
                  </Grid>
                </Grid>
              </>
            )}
          </Box>
        </SwipeableDrawer>
      ) : (
        <Dialog
          open={open}
          onClose={handleReset}
          maxWidth="sm"
          fullWidth
          sx={{
            "& .MuiPaper-root": {
              borderRadius: 2,
              p: 2,
              boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.2)",
            },
          }}
        >
          <DialogTitle>
            <DialogTitleContent />
          </DialogTitle>
          {isLoading ? (
            <LinearLoadingComponent />
          ) : (
            <>
              <DialogContent>{ImageReportForm}</DialogContent>
              <DialogActions>
                <Button variant="outlined" sx={getButtonStyle("close")} onClick={handleReset}>
                  {t("cancel")}
                </Button>
                {!request && (
                  <Button
                    variant="outlined"
                    sx={getButtonStyle("open")}
                    disabled={isLoading || (location.pathname === "/my-certifications" && (!source || !selectedTag)) || (location.pathname !== "/my-certifications" && !source)}
                    onClick={() => setApproval(true)}
                  >
                    {t("send_request")}
                  </Button>
                )}
                <Button variant="outlined" sx={getButtonStyle("open")} disabled={isLoading || !source} onClick={() => setConfirm(true)}>
                  {t("certify")}
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>
      )}

      <NewCertConfirm confirm={confirm} setConfirm={setConfirm} handleGenerate={handleGenerate} />

      <NewRequestConfirm approval={approval} setApproval={setApproval} handleSendApprovalRequest={handleSendApprovalRequest} />

      <InvalidFile open={isInvalidFile} setOpen={setIsInvalidFile} message={errorMessage} />

      {selectedImage && (
        <Dialog
          open={Boolean(selectedImage)}
          onClose={handleCloseDialogImage}
          fullWidth
          maxWidth="sm"
          sx={{
            "& .MuiPaper-root": {
              borderRadius: "15px",
              padding: 0,
              boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.5)",
            },
          }}
        >
          <Tooltip placement="top" title={t("close")}>
            <IconButton
              onClick={handleCloseDialogImage}
              color="error"
              sx={{
                position: "absolute",
                top: "0%",
                right: "0%",
                zIndex: 1,
              }}
              aria-label="close"
            >
              <CloseOutlinedIcon />
            </IconButton>
          </Tooltip>
          <img src={selectedImage} alt="Preview" style={{ width: "100%" }} />
        </Dialog>
      )}
    </>
  );
};

export default ImageGenerator;
